import {
  mdiViewDashboard,
  mdiPlayBoxOutline,
  mdiChartLine,
  mdiWeb,
  mdiCartPlus,
  mdiDiscord,
  mdiTuneVertical,
  mdiShieldOutline,
  mdiCart,
  mdiReceipt,
  mdiAccountGroupOutline,
  mdiEmailCheck,
  mdiAccountCircle,
  mdiLifebuoy,
  mdiCogOutline,
  mdiCreditCardOutline,
  mdiSchoolOutline
} from "@mdi/js";

/*
 * Two tiers, not four sections.
 *
 * The four destinations opened every session sit flat and unlabelled — a
 * heading over an always-visible list of four is a row of type that earns
 * nothing. Everything occasional (set once at signup, or an errand a few times
 * a year) folds into one of two groups, so the rail is 7 rows at rest instead
 * of 13 and no longer overflows its own scroll area.
 *
 * Nothing was deleted. Every route that was reachable from the sidebar before
 * still is; the occasional ones are one click deeper.
 *
 * `DIVIDER` marks the tier boundary — AsideMenu.vue renders it as a rule at
 * both sidebar widths. Anything else that is a plain string is still a section
 * heading, exactly as before.
 *
 * `match` lists the routes an item stands for besides its own `to`: an entry
 * ending in "/" claims everything under it, anything else must match exactly.
 * Without it the sidebar only lit up on an exact path, so a campaign's logs,
 * the payment steps and a site's subscription page highlighted nothing and
 * left their group shut. A child's own `to` still wins over a parent's
 * `match` (AsideMenuItem.vue), so /campaigns/chart lights "Campaign Chart",
 * not "Campaigns".
 */
export const DIVIDER = "---";

const links = [
  // Verify section (filtered out once verified in App.vue — that filter matches
  // on the "Verify" heading and on `item[0].to === '/verify'`, so this section
  // has to stay shaped exactly like this and stay first).
  "Verify",
  [{
    to: "/verify",
    labelKey: 'nav.verifyEmail',
    icon: mdiEmailCheck,
    updateMark: true,
  }],

  // ── Tier 1: every session. No heading; the list is short enough to read as
  // one thing, and these four are what the product is for.
  [
    {
      to: '/dashboard',
      labelKey: 'nav.dashboard',
      icon: mdiViewDashboard,
    },
    {
      /* Both `to` and `menu`: AsideMenuItem renders a parent with a `to` as a
         router-link and still toggles its dropdown on click, so "Campaigns"
         keeps going to the job list while revealing what sits under it. The
         chevron is what advertises the child, and `hasActiveChild` keeps the
         parent lit while the child route is open. */
      to: "/campaigns",
      labelKey: 'nav.campaigns',
      icon: mdiPlayBoxOutline,
      // A campaign's logs, its per-campaign chart and grouped view all live
      // under /logs/.
      match: ['/campaigns/', '/logs/'],
      menu: [
        {
          to: "/campaigns/chart",
          labelKey: 'nav.campaignChart',
          icon: mdiChartLine,
        },
      ],
    },
    {
      to: "/websites",
      labelKey: 'nav.websites',
      icon: mdiWeb,
      // /websites/:id/subscription
      match: ['/websites/'],
    },
    {
      to: '/discord-tokens',
      labelKey: 'nav.discordTokens',
      icon: mdiDiscord,
    },
  ],

  DIVIDER,

  // ── Tier 2: occasional. Two groups with no `to` of their own, so clicking
  // one only opens it. Collapsed by default; AsideMenuItem opens whichever
  // group holds the current route, and leaves a group the user opened by hand
  // open as they move around.
  [
    {
      labelKey: 'nav.settings',
      icon: mdiCogOutline,
      menu: [
        {
          to: '/advanced/profiles',
          labelKey: 'nav.voteProfiles',
          icon: mdiTuneVertical,
        },
        {
          to: '/advanced/profiles/proxy',
          labelKey: 'nav.proxyProfiles',
          icon: mdiShieldOutline,
        },
        {
          to: "/account",
          labelKey: 'nav.account',
          icon: mdiAccountCircle,
        },
        {
          to: "/tutorial",
          labelKey: 'nav.tutorial',
          icon: mdiSchoolOutline,
        },
        {
          /* Kept in the tree rather than folded into the footer's Help button:
             that button opens Crisp chat, which is not the same thing as the
             /support page, and dropping this row would have left the route
             reachable only by URL. */
          to: "/support",
          labelKey: 'nav.support',
          icon: mdiLifebuoy,
        },
      ],
    },
    {
      labelKey: 'nav.billing',
      icon: mdiCreditCardOutline,
      menu: [
        {
          to: "/products",
          labelKey: 'nav.buyTokens',
          icon: mdiCartPlus,
          // The payment-method step, and where Stripe returns once it is paid.
          match: ['/replenish', '/success'],
        },
        {
          /* The cart belongs here rather than in tier 1: it is transient state,
             not a place, and every page that puts something in it links
             onward. The top bar also shows a cart button with the item count
             whenever the cart is non-empty (NavBar.vue); this row stays so the
             route is always reachable from the menu too. */
          to: "/checkout",
          labelKey: 'nav.cart',
          icon: mdiCart,
        },
        {
          to: "/invoices",
          labelKey: 'nav.invoices',
          icon: mdiReceipt,
        },
        {
          href: "https://toplistbots.tolt.io/login",
          labelKey: 'nav.affiliates',
          target: "_blank",
          icon: mdiAccountGroupOutline,
        },
      ],
    },
  ],
];

export default links
